/**
 * Admin translations
 * 
 * This file contains translations for admin features: feedback and event management.
 */

import { AdminTranslationType } from './types';

// English translations
export const enAdmin: AdminTranslationType = {
  admin: {
    feedback: {
      auth: {
        signInRequired: "Please Sign In",
        signInMessage: "You need to be signed in to access this page.",
        goHome: "Go to Home",
        accessDenied: "Access Denied",
        adminRequired: "You need admin privileges to access this page."
      },
      header: {
        backHome: "Back to Home",
        title: "Feedback Management",
        adminBadge: "Admin"
      },
      stats: {
        total: "Total",
        new: "New",
        bugReports: "Bug Reports",
        featureRequests: "Feature Requests"
      },
      filters: {
        searchPlaceholder: "Search feedback...",
        allFeedback: "All Feedback",
        typeLabel: "Type",
        statusLabel: "Status"
      },
      types: {
        general: "General",
        bug: "Bug Report",
        feature: "Feature Request",
        ui: "UI/UX"
      },
      status: {
        new: "New",
        read: "Read",
        "in-progress": "In Progress",
        resolved: "Resolved"
      },
      emptyState: {
        title: "No feedback found",
        noMatches: "No feedback matches your search or filter criteria.",
        noSubmissions: "No feedback has been submitted yet."
      },
      pagination: {
        showing: "Showing {{start}} to {{end}} of {{total}} results",
        previous: "Previous page",
        next: "Next page",
        page: "Page {{page}}"
      },
      deleteModal: {
        title: "Delete Feedback",
        confirmation: "Are you sure you want to delete this feedback? This action cannot be undone.",
        from: "From",
        cancel: "Cancel",
        delete: "Delete"
      }
    },
    events: {
      auth: {
        signInRequired: "Please Sign In",
        signInMessage: "You need to be signed in to access this page.",
        goHome: "Go to Home",
        accessDenied: "Access Denied",
        adminRequired: "You need admin privileges to access this page."
      },
      header: {
        title: "Event Management",
        adminBadge: "Admin",
        refresh: "Refresh"
      },
      stats: {
        totalEvents: "Total Events",
        active: "Active",
        closed: "Closed",
        totalUsers: "Total Users",
        totalSpent: "Total Spent"
      },
      filters: {
        searchPlaceholder: "Search by event name, ID, or participant..."
      },
      sort: {
        date: "Date",
        name: "Name",
        participants: "Participants",
        amount: "Amount"
      },
      table: {
        expenses: "expenses"
      },
      detail: {
        eventId: "Event ID",
        createdAt: "Created At",
        participants: "Participants"
      },
      actions: {
        viewEvent: "View Event",
        delete: "Delete",
        clone: "Clone for testing",
        cloning: "Cloning..."
      },
      emptyState: {
        title: "No events found",
        noMatches: "No events match your search criteria.",
        noEvents: "No events have been created yet."
      },
      pagination: {
        showing: "Showing {{start}} to {{end}} of {{total}} results"
      },
      deleteModal: {
        title: "Delete Event",
        confirmation: "Are you sure you want to delete this event? This will permanently remove all expenses, participants, and settlement data. This action cannot be undone.",
        cancel: "Cancel",
        delete: "Delete Event",
        error: "Failed to delete event. Please try again."
      },
      clone: {
        badge: "TEST CLONE",
        hint: "Creates an independent copy under a new id. The source event is only read, never written to.",
        success: "Clone created",
        summary: "{{participants}} participants, {{expenses}} entries — identical splits to the source.",
        ownerSeat: "You now hold the original owner's seat in the clone.",
        openClone: "Open clone",
        copyId: "Copy id",
        copied: "Copied",
        dismiss: "Dismiss",
        error: "Failed to clone event."
      }
    },
    users: {
      auth: {
        signInRequired: "Please Sign In",
        signInMessage: "You need to be signed in to access this page.",
        goHome: "Go to Home",
        accessDenied: "Access Denied",
        adminRequired: "You need admin privileges to access this page."
      },
      header: {
        title: "User Management",
        adminBadge: "Admin",
        refresh: "Refresh"
      },
      stats: {
        totalUsers: "Total Users",
        withBankInfo: "With Bank Info",
        recentSignups: "Recent (7d)",
        totalEvents: "Total Events"
      },
      filters: {
        searchPlaceholder: "Search by name, email, or UID..."
      },
      sort: {
        joinDate: "Join Date",
        name: "Name",
        email: "Email"
      },
      detail: {
        uid: "User ID",
        email: "Email",
        joinDate: "Joined",
        bankInfo: "Bank Information",
        noBankInfo: "No bank info added",
        eventSelections: "Event Selections",
        noEventSelections: "No event selections",
        bankDetails: {
          bankName: "Bank",
          accountNumber: "Account Number",
          accountHolder: "Account Holder"
        }
      },
      emptyState: {
        title: "No users found",
        noMatches: "No users match your search criteria.",
        noUsers: "No users have registered yet."
      },
      pagination: {
        showing: "Showing {{start}} to {{end}} of {{total}} results"
      }
    }
  }
};

// Vietnamese translations
export const viAdmin: AdminTranslationType = {
  admin: {
    feedback: {
      auth: {
        signInRequired: "Vui Lòng Đăng Nhập",
        signInMessage: "Bạn cần đăng nhập để truy cập trang này.",
        goHome: "Về Trang Chủ",
        accessDenied: "Từ Chối Truy Cập",
        adminRequired: "Bạn cần quyền quản trị để truy cập trang này."
      },
      header: {
        backHome: "Quay Lại Trang Chủ",
        title: "Quản Lý Phản Hồi",
        adminBadge: "Quản Trị"
      },
      stats: {
        total: "Tổng Cộng",
        new: "Mới",
        bugReports: "Báo Cáo Lỗi",
        featureRequests: "Yêu Cầu Tính Năng"
      },
      filters: {
        searchPlaceholder: "Tìm kiếm phản hồi...",
        allFeedback: "Tất Cả Phản Hồi",
        typeLabel: "Loại",
        statusLabel: "Trạng Thái"
      },
      types: {
        general: "Chung",
        bug: "Báo Cáo Lỗi",
        feature: "Yêu Cầu Tính Năng",
        ui: "Giao Diện/UX"
      },
      status: {
        new: "Mới",
        read: "Đã Đọc",
        "in-progress": "Đang Xử Lý",
        resolved: "Đã Giải Quyết"
      },
      emptyState: {
        title: "Không tìm thấy phản hồi",
        noMatches: "Không có phản hồi nào phù hợp với tiêu chí tìm kiếm hoặc lọc của bạn.",
        noSubmissions: "Chưa có phản hồi nào được gửi."
      },
      pagination: {
        showing: "Hiển thị {{start}} đến {{end}} của {{total}} kết quả",
        previous: "Trang trước",
        next: "Trang sau",
        page: "Trang {{page}}"
      },
      deleteModal: {
        title: "Xóa Phản Hồi",
        confirmation: "Bạn có chắc chắn muốn xóa phản hồi này không? Hành động này không thể hoàn tác.",
        from: "Từ",
        cancel: "Hủy",
        delete: "Xóa"
      }
    },
    events: {
      auth: {
        signInRequired: "Vui Lòng Đăng Nhập",
        signInMessage: "Bạn cần đăng nhập để truy cập trang này.",
        goHome: "Về Trang Chủ",
        accessDenied: "Từ Chối Truy Cập",
        adminRequired: "Bạn cần quyền quản trị để truy cập trang này."
      },
      header: {
        title: "Quản Lý Sự Kiện",
        adminBadge: "Quản Trị",
        refresh: "Làm Mới"
      },
      stats: {
        totalEvents: "Tổng Sự Kiện",
        active: "Đang Hoạt Động",
        closed: "Đã Đóng",
        totalUsers: "Tổng Người Dùng",
        totalSpent: "Tổng Chi Tiêu"
      },
      filters: {
        searchPlaceholder: "Tìm theo tên sự kiện, ID, hoặc người tham gia..."
      },
      sort: {
        date: "Ngày",
        name: "Tên",
        participants: "Người",
        amount: "Số Tiền"
      },
      table: {
        expenses: "chi phí"
      },
      detail: {
        eventId: "ID Sự Kiện",
        createdAt: "Ngày Tạo",
        participants: "Người Tham Gia"
      },
      actions: {
        viewEvent: "Xem Sự Kiện",
        delete: "Xóa",
        clone: "Nhân bản để test",
        cloning: "Đang nhân bản..."
      },
      emptyState: {
        title: "Không tìm thấy sự kiện",
        noMatches: "Không có sự kiện nào phù hợp với tiêu chí tìm kiếm.",
        noEvents: "Chưa có sự kiện nào được tạo."
      },
      pagination: {
        showing: "Hiển thị {{start}} đến {{end}} của {{total}} kết quả"
      },
      deleteModal: {
        title: "Xóa Sự Kiện",
        confirmation: "Bạn có chắc chắn muốn xóa sự kiện này không? Tất cả chi phí, người tham gia và dữ liệu thanh toán sẽ bị xóa vĩnh viễn. Hành động này không thể hoàn tác.",
        cancel: "Hủy",
        delete: "Xóa Sự Kiện",
        error: "Không thể xóa sự kiện. Vui lòng thử lại."
      },
      clone: {
        badge: "BẢN TEST",
        hint: "Tạo một bản sao độc lập với id mới. Sự kiện gốc chỉ được đọc, không bị ghi vào.",
        success: "Đã tạo bản nhân bản",
        summary: "{{participants}} người tham gia, {{expenses}} khoản — cách chia y hệt bản gốc.",
        ownerSeat: "Bạn đang giữ chỗ của chủ sự kiện gốc trong bản nhân bản.",
        openClone: "Mở bản nhân bản",
        copyId: "Copy id",
        copied: "Đã copy",
        dismiss: "Đóng",
        error: "Không thể nhân bản sự kiện."
      }
    },
    users: {
      auth: {
        signInRequired: "Vui Lòng Đăng Nhập",
        signInMessage: "Bạn cần đăng nhập để truy cập trang này.",
        goHome: "Về Trang Chủ",
        accessDenied: "Từ Chối Truy Cập",
        adminRequired: "Bạn cần quyền quản trị để truy cập trang này."
      },
      header: {
        title: "Quản Lý Người Dùng",
        adminBadge: "Quản Trị",
        refresh: "Làm Mới"
      },
      stats: {
        totalUsers: "Tổng Người Dùng",
        withBankInfo: "Có TK Ngân Hàng",
        recentSignups: "Mới (7 ngày)",
        totalEvents: "Tổng Sự Kiện"
      },
      filters: {
        searchPlaceholder: "Tìm theo tên, email, hoặc UID..."
      },
      sort: {
        joinDate: "Ngày Tham Gia",
        name: "Tên",
        email: "Email"
      },
      detail: {
        uid: "ID Người Dùng",
        email: "Email",
        joinDate: "Ngày Tham Gia",
        bankInfo: "Thông Tin Ngân Hàng",
        noBankInfo: "Chưa thêm thông tin ngân hàng",
        eventSelections: "Sự Kiện Đã Chọn",
        noEventSelections: "Chưa chọn sự kiện nào",
        bankDetails: {
          bankName: "Ngân Hàng",
          accountNumber: "Số Tài Khoản",
          accountHolder: "Chủ Tài Khoản"
        }
      },
      emptyState: {
        title: "Không tìm thấy người dùng",
        noMatches: "Không có người dùng nào phù hợp với tiêu chí tìm kiếm.",
        noUsers: "Chưa có người dùng nào đăng ký."
      },
      pagination: {
        showing: "Hiển thị {{start}} đến {{end}} của {{total}} kết quả"
      }
    }
  }
};